import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { Subject, Subscription, combineLatest, map, of, timer } from 'rxjs';
import { catchError, switchMap, takeUntil } from 'rxjs/operators';
import { Popover, PopoverModule } from 'primeng/popover';
import { Permissions } from '@app/core/guard/Permissions';
import { Roles } from '@app/core/guard/Roles';
import { AccountingDocumentListPage } from '@interfaces/accounting-data-standardization/responses/accounting-document-list-page.response';
import { DateFormatterService } from '@services/helpers/date-formatter.service';
import { PermissionsService } from '@services/helpers/permissions.service';
import { AccountingDataStandardizationService } from '@services/http/accounting-data-standardization/accounting-data-standardization.service';
import { AccountingProgressTimelineComponent } from '@shared/components/accounting-progress-timeline/accounting-progress-timeline.component';
import { AccountingProgressPercentComponent } from '@shared/components/accounting-progress-percent/accounting-progress-percent.component';
import { progressLabel } from '@shared/utils/accounting-progress.util';
import {
  DATA_ROOM_DOCUMENT_LINK_ROUTE,
  dataRoomDocumentLinkParams,
} from '@shared/utils/data-room-document-link.util';
import { isPlainLeftClick } from '@shared/utils/plain-click.util';
import { DfpStandardizationNavigationService } from '@src/app/features/dashboard/performance/pages/dfp/services/dfp-standardization-navigation.service';
import {
  DFP_PROCESSING_FAST_POLL_MS,
  DFP_PROCESSING_STAGE_LABEL,
  DFP_PROCESSING_STATUSES,
  DfpProcessingDocument,
  buildProcessingSummary,
  hasProcessingSetChanged,
  historicalMessage,
  nextProcessingPollDelay,
  splitProcessingDocuments,
  toProcessingDocuments,
} from '@src/app/features/dashboard/performance/pages/dfp/utils/dfp-processing-documents.util';

/**
 * Aviso de documentos contábeis em processamento da Análise Econômica (DFP Histórica).
 *
 * Reprocessar um documento contábil tira a validação dele, e documento sem validação sai da disputa de "qual
 * documento vale em cada ano" até ser validado de novo. Enquanto isso a coluna daquele ano mostra outro
 * documento, e quem olha não sabe que é temporário. Este componente avisa, lista os documentos num popover e
 * recarrega a tela (`processingSetChanged`) quando um deles entra ou sai do conjunto em andamento.
 *
 * ## De onde vêm os dados
 *
 * A mesma lista da Padronização (`GET accounting-document-management/documents`) filtrada pela empresa e pelos
 * status em andamento: ela já traz status, `deParaState`, datas e o documento do Data Room. O resumo
 * `processing-status/summary`, usado pelo estado vazio, não serve aqui: não diz QUAIS documentos nem conta quem
 * está esperando validação.
 *
 * ## Polling
 *
 * Mesmo desenho do `DfpEmptyStateComponent` (`catchError` DENTRO do `switchMap`, três falhas seguidas encerram),
 * com intervalo que depende do que está em andamento (`nextProcessingPollDelay`): 10s com leitura ou De/Para
 * rodando, 30s quando só há documento esperando validação, e para quando sobra só erro ou nada. Sai da tela,
 * troca de empresa ou o componente é destruído: o ciclo para.
 */
@Component({
  selector: 'app-dfp-processing-documents-notice',
  standalone: true,
  imports: [
    PopoverModule,
    RouterLink,
    AccountingProgressTimelineComponent,
    AccountingProgressPercentComponent,
  ],
  templateUrl: './processing-documents-notice.component.html',
  styleUrls: ['./processing-documents-notice.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProcessingDocumentsNoticeComponent implements OnChanges, OnDestroy {
  private readonly standardizationService = inject(AccountingDataStandardizationService);
  private readonly dateFormatter = inject(DateFormatterService);
  private readonly permissions = inject(PermissionsService);
  protected readonly navigation = inject(DfpStandardizationNavigationService);

  /** Falhas seguidas da consulta que encerram o polling. */
  private static readonly MAX_CONSECUTIVE_FAILURES = 3;

  /** Teto da página pedida. Documentos em andamento numa empresa são poucos; 100 cobre com folga. */
  private static readonly PAGE_SIZE = 100;

  private readonly destroy$ = new Subject<void>();
  private pollSubscription: Subscription | null = null;
  private consecutiveFailures = 0;

  /** Ids do conjunto na consulta anterior; `null` antes da primeira resposta (a primeira não recarrega a tela). */
  private previousIds: string[] | null = null;

  /** Empresa selecionada na tela. Trocar reinicia tudo. */
  @Input({ required: true }) companyId!: string;

  /** Algum documento entrou ou saiu do conjunto em andamento: a tela deve recarregar os dados da DFP. */
  @Output() processingSetChanged = new EventEmitter<void>();

  /** Documentos em andamento da empresa, já com a etapa em linguagem de negócio (os dois grupos juntos). */
  readonly documents = signal<DfpProcessingDocument[]>([]);

  /**
   * "Pede ação" e "Histórico" (card 3708): a classificação vem pronta do backend (`analysisSituation`), a tela só
   * separa. Versões antigas de um período que já tem documento validado não pedem validação de ninguém.
   */
  private readonly groups = computed(() => splitProcessingDocuments(this.documents()));

  /** Documentos que pedem ação: são eles que fazem o aviso aparecer, entram na contagem e na frase. */
  readonly actionableDocuments = computed(() => this.groups().actionable);

  /** Documentos que ficam só como histórico: bloco recolhido no fim do popover. */
  readonly historicalDocuments = computed(() => this.groups().historical);

  /** Bloco "Histórico" aberto no popover. Começa recolhido e volta a recolher ao trocar de empresa. */
  readonly historyExpanded = signal(false);

  /** Frase do aviso, só com quem pede ação. */
  readonly summary = computed(() => buildProcessingSummary(this.actionableDocuments()));

  /** Há leitura ou De/Para rodando agora (o ícone gira só nesse caso). */
  readonly hasActiveWork = computed(() =>
    this.actionableDocuments().some(doc => doc.stage === 'reading' || doc.stage === 'mapping')
  );

  readonly historicalMessage = historicalMessage;

  readonly stageLabel = DFP_PROCESSING_STAGE_LABEL;
  readonly standardizationRoute = DfpStandardizationNavigationService.ROUTE;
  readonly dataRoomRoute = DATA_ROOM_DOCUMENT_LINK_ROUTE;

  /** Popover "Ver documentos": fecha antes de a modal abrir (ver `onStandardizationDocumentClick`). */
  private readonly panel = viewChild<Popover>('panel');

  /**
   * Os links para a Padronização seguem a regra do `side-menu` para aquele item: role ADMIN (o grupo Back-office
   * exige ADMIN ou TENANT, e o item exige ADMIN) E permissão `USER_CREATE`. Quem não vê a tela no menu não recebe
   * o atalho, que bateria numa tela que ele não deveria abrir.
   */
  readonly canOpenStandardization = toSignal(
    combineLatest([
      this.permissions.hasRole(Roles.ADMIN),
      this.permissions.hasPermission(Permissions.User.CREATE),
    ]).pipe(map(([hasRole, hasPermission]) => hasRole && hasPermission)),
    { initialValue: false }
  );

  /** "Abrir no Data Room" segue o item "Data Room" do menu, que exige `DATAROOM_MODULE`. */
  readonly canOpenDataRoom = toSignal(this.permissions.hasPermission(Permissions.DataRoom.MODULE), {
    initialValue: false,
  });

  /**
   * Documento com a linha do tempo aberta no popover (um por vez, para o painel não crescer). A linha do tempo é o
   * mesmo componente do Data Room e da Padronização, na versão compacta, e consulta a cada 5 s enquanto a esteira anda.
   */
  readonly expandedId = signal<string | null>(null);

  ngOnChanges(): void {
    this.restart();
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** `href` do "Abrir na Padronização": a tela completa com o documento aberto (nova guia, Ctrl+clique, menu). */
  standardizationHref(doc: DfpProcessingDocument): string {
    return this.navigation.documentHref(this.companyId, doc.item.id);
  }

  /**
   * Clique em "Abrir na Padronização". O simples (botão esquerdo, sem modificador) abre o documento na MESMA modal
   * do "Ver De/Para" do menu da coluna de ano, pelo mesmo caminho (`openDocumentModal` → `documentModalRequests$` →
   * modal única do `DfpComponent`, que recarrega a DFP ao fechar se algo foi gravado). Aba "Dados extraídos": o
   * documento pode estar sendo lido e ainda não ter De/Para.
   *
   * Os demais (Ctrl/Cmd, Shift, Alt) seguem o `href` em nova guia, sem tocar no filtro global de empresa: nova guia
   * é entrada externa, e a Padronização obedece ao `companyId` da URL.
   */
  onStandardizationDocumentClick(event: MouseEvent, doc: DfpProcessingDocument): void {
    if (!isPlainLeftClick(event)) return;
    event.preventDefault();
    // O popover fica no `body` e captaria o clique-fora que fecha a modal (mesmo cuidado do menu da coluna de ano).
    this.panel()?.hide();
    this.navigation.openDocumentModal(this.companyId, doc.item.id, null, null);
  }

  /**
   * Query params do "Abrir no Data Room": link direto sem a pasta (`/data-room/document?doc=&panel=details&section=progress`),
   * que descobre a pasta do documento e abre o painel de detalhes no bloco "Andamento". Sem a pasta porque a lista só
   * traz o id do documento, e resolver a pasta de cada um a cada tick do polling seria uma consulta por documento.
   * `companyId` é a empresa de reserva, para quando o detalhe não trouxer a dele.
   */
  dataRoomQueryParams(dataRoomDocumentId: string): Record<string, string> {
    return {
      companyId: this.companyId,
      ...dataRoomDocumentLinkParams({
        documentId: dataRoomDocumentId,
        panel: 'details',
        section: 'progress',
      }),
    };
  }

  /** Query params do "Ver no back-office": a lista da Padronização na empresa e nos status em andamento. */
  listQueryParams(): Record<string, string> {
    return this.navigation.listQueryParams(this.companyId, DFP_PROCESSING_STATUSES);
  }

  /**
   * Clique no "Ver no back-office": grava a empresa no filtro global (ver o serviço), só no clique simples, que é
   * navegação interna. Ctrl/Cmd+clique abre nova guia, entrada externa que já obedece ao `companyId` da URL; gravar
   * ali trocaria à toa o filtro de empresa das outras telas desta guia.
   */
  onStandardizationListClick(event: MouseEvent): void {
    if (isPlainLeftClick(event)) this.navigation.rememberCompanyFilter(this.companyId);
  }

  /**
   * Marco atual detalhado do documento ("Execução 6 de 40", "De/Para: 120 de 225 linhas"), vindo pronto do backend no
   * `progress` do item. Vazio sem o campo (backend anterior): o popover fica como era, só com a etapa.
   */
  progressText(doc: DfpProcessingDocument): string {
    return progressLabel(doc.item.progress);
  }

  /** O documento tem andamento para mostrar na linha do tempo (o backend mandou o `progress`). */
  hasProgress(doc: DfpProcessingDocument): boolean {
    return doc.item.progress !== undefined;
  }

  toggleHistory(): void {
    this.historyExpanded.update(expanded => !expanded);
  }

  toggleTimeline(doc: DfpProcessingDocument): void {
    this.expandedId.update(id => (id === doc.item.id ? null : doc.item.id));
  }

  /**
   * A linha do tempo viu a esteira de um documento terminar: consulta a lista agora, sem esperar o próximo ciclo, para
   * a etapa do popover e a recarga da tela (quando o conjunto muda) acontecerem na hora.
   */
  onTimelineSettled(): void {
    if (this.companyId) {
      this.consecutiveFailures = 0;
      this.schedule(0);
    }
  }

  /** "Desde quando", por etapa. */
  sinceLabel(doc: DfpProcessingDocument): string {
    const when = doc.item.processedAt
      ? this.dateFormatter.formatDateTimeMinutesInstant(doc.item.processedAt)
      : '';
    if (!when) return '';
    if (doc.stage === 'awaitingValidation') return `Extraído em ${when}`;
    if (doc.stage === 'error') return `Parou em ${when}`;
    return `Desde ${when}`;
  }

  private restart(): void {
    this.stopPolling();
    this.documents.set([]);
    this.expandedId.set(null);
    this.historyExpanded.set(false);
    this.previousIds = null;
    this.consecutiveFailures = 0;
    if (!this.companyId) return;
    this.schedule(0);
  }

  /**
   * Agenda UMA consulta. O próximo intervalo só é decidido com a resposta na mão (`applyPage`), por isso não é um
   * `timer(0, n)` fixo como no estado vazio.
   */
  private schedule(delayMs: number): void {
    this.stopPolling();
    this.pollSubscription = timer(delayMs)
      .pipe(
        switchMap(() =>
          this.standardizationService
            .getDocuments({
              companyIds: [this.companyId],
              page: 1,
              pageSize: ProcessingDocumentsNoticeComponent.PAGE_SIZE,
              statuses: DFP_PROCESSING_STATUSES,
            })
            .pipe(catchError(() => of(null)))
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(page => this.applyPage(page));
  }

  private applyPage(page: AccountingDocumentListPage | null): void {
    if (!page) {
      // Consulta acessória: falhar não apaga o aviso que está na tela nem gera toast. Três falhas seguidas
      // encerram o ciclo para um endpoint quebrado não virar polling eterno.
      this.consecutiveFailures += 1;
      if (this.consecutiveFailures < ProcessingDocumentsNoticeComponent.MAX_CONSECUTIVE_FAILURES) {
        this.schedule(
          nextProcessingPollDelay(this.actionableDocuments()) ?? DFP_PROCESSING_FAST_POLL_MS
        );
      } else {
        this.stopPolling();
      }
      return;
    }

    this.consecutiveFailures = 0;
    const documents = toProcessingDocuments(page.data);
    const currentIds = documents.map(doc => doc.item.id);

    if (this.previousIds && hasProcessingSetChanged(this.previousIds, currentIds)) {
      this.processingSetChanged.emit();
    }
    this.previousIds = currentIds;
    this.documents.set(documents);

    // O intervalo olha só quem pede ação: histórico não anda sozinho nem espera validação de ninguém, então
    // empresa só com histórico para de consultar (como "só erro"). O conjunto acima conta os dois grupos.
    const delay = nextProcessingPollDelay(this.actionableDocuments());
    if (delay === null) {
      this.stopPolling();
    } else {
      this.schedule(delay);
    }
  }

  private stopPolling(): void {
    this.pollSubscription?.unsubscribe();
    this.pollSubscription = null;
  }
}
